import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Loader2 } from 'lucide-react';

const fmt = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('id-ID', { maximumFractionDigits: 2 });
};

export default function AsaArrivalConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  requestData,
  inventoryMaster = {},
  isProcessing = false
}) {
  const [ekspedisi, setEkspedisi] = useState('Ekspedisi Vendor / Langsung');
  const [catatan, setCatatan] = useState('');

  // Extract items list (single or batch)
  const items = useMemo(() => {
    if (!requestData) return [];
    let list = [];
    try {
      list = typeof requestData.batch_items === 'string' 
        ? JSON.parse(requestData.batch_items) 
        : (requestData.batch_items || []);
    } catch (e) {
      list = [];
    }

    if (!list || list.length === 0) {
      list = [{
        id: requestData.material_id,
        nama: requestData.nama_item || requestData.item_name || 'Item Purchasing',
        qty: parseFloat(String(requestData.qty_beli || requestData.jumlah || 1).replace(',', '.')),
        satuan: requestData.satuan || (requestData.kategori === 'Bahan Cair' ? 'kg' : 'pcs'),
        type: requestData.kategori === 'Bahan Cair' ? 'bahan-cair' : (requestData.kategori?.toLowerCase() || 'bahan-cair'),
        kategori: requestData.kategori || 'Bahan Cair',
        harga: requestData.harga_unit || requestData.harga_rupiah || requestData.harga_beli || 0
      }];
    } else {
      list = list.map(it => ({
        id: it.id || it.material_id,
        nama: it.name || it.nama || it.nama_barang || 'Item Purchasing',
        qty: parseFloat(String(it.qty || it.jumlah || 1).replace(',', '.')),
        satuan: it.satuan || 'pcs',
        type: it.type || (it.kategori === 'Bahan Cair' ? 'bahan-cair' : (it.kategori?.toLowerCase() || 'bahan-cair')),
        kategori: it.kategori || 'Bahan',
        harga: it.harga_rupiah || it.harga_beli || it.harga || 0
      }));
    }

    // Resolve inventory status and stock for each item
    const { 
      bahanCairList = [], 
      botolList = [], 
      tutupList = [], 
      sprayList = [], 
      fullsetList = [], 
      itemTambahanList = [] 
    } = inventoryMaster;

    return list.map(item => {
      const itemName = (item.nama || '').trim().toLowerCase();
      const itemType = (item.type || item.kategori || '').toLowerCase();
      const itemId = item.id;

      let matched = null;
      let masterCategory = 'Bahan Cair';

      if (itemType.includes('cair') || itemType.includes('bibit') || itemType.includes('campuran') || itemType === 'bahan-cair') {
        matched = bahanCairList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        masterCategory = 'Bahan Cair';
      } else if (itemType.includes('botol')) {
        matched = botolList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        masterCategory = 'Botol';
      } else if (itemType.includes('tutup')) {
        matched = tutupList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        masterCategory = 'Tutup';
      } else if (itemType.includes('spray')) {
        matched = sprayList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        masterCategory = 'Spray';
      } else if (itemType.includes('fullset')) {
        matched = fullsetList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName));
        masterCategory = 'Fullset';
      } else {
        matched = itemTambahanList.find(b => (itemId && String(b.id) === String(itemId)) || (b.nama && b.nama.trim().toLowerCase() === itemName))
          || bahanCairList.find(b => b.nama && b.nama.trim().toLowerCase() === itemName)
          || botolList.find(b => b.nama && b.nama.trim().toLowerCase() === itemName);
        masterCategory = 'Item Tambahan';
      }

      const isRegistered = !!matched;
      const currentStock = matched ? (parseFloat(String(matched.stok || matched.total_stock || 0).replace(',', '.')) || 0) : 0;
      const incomingQty = item.qty || 0;
      const finalStock = currentStock + incomingQty;
      
      // Determine inventory status: Aktif, Pasif, or Non-Aktif
      let inventoryStatus = 'Aktif';
      let statusSymbol = '●';
      let statusTheme = 'active'; // 'active', 'passive', 'inactive', 'new'

      if (matched) {
        const rawStatus = (matched.status_aktif || matched.status || '').toLowerCase();
        if (rawStatus.includes('non') || matched.is_active === false) {
          inventoryStatus = 'Non-Aktif';
          statusSymbol = '✕';
          statusTheme = 'inactive';
        } else if (rawStatus.includes('pasif') || currentStock <= 0) {
          inventoryStatus = 'Pasif (Stok Kosong)';
          statusSymbol = '○';
          statusTheme = 'passive';
        } else {
          inventoryStatus = 'Aktif';
          statusSymbol = '●';
          statusTheme = 'active';
        }
      } else {
        inventoryStatus = 'Item Baru';
        statusSymbol = '✦';
        statusTheme = 'new';
      }

      return {
        ...item,
        isRegistered,
        matchedId: matched?.id,
        masterCategory,
        currentStock,
        incomingQty,
        finalStock,
        inventoryStatus,
        statusSymbol,
        statusTheme
      };
    });
  }, [requestData, inventoryMaster]);

  if (!isOpen || !requestData) return null;

  const handleConfirmArrival = () => {
    onConfirm({
      ekspedisi: ekspedisi.trim(),
      catatan: catatan.trim(),
      items
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden rounded-3xl border-0 shadow-2xl z-[100] bg-white">
        {/* ASA Signature Gradient Header */}
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-[#1E293B] p-6 text-white text-center relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="absolute -left-8 -bottom-8 w-36 h-36 bg-blue-400/20 rounded-full blur-2xl pointer-events-none"></div>

          {/* ASA Avatar Badge */}
          <div className="relative w-16 h-16 mx-auto mb-3">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center p-1 border-2 border-white/40 shadow-lg backdrop-blur-md">
              <img src="/asa-profile.png?v=3" alt="ASA AI" className="w-full h-full object-cover rounded-full" />
            </div>
            <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-400 border-2 border-white rounded-full"></span>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-blue-100 text-[11px] font-bold mb-1.5 backdrop-blur-sm tracking-wide">
            <span>✦</span>
            <span>Konfirmasi Kedatangan Barang Gudang</span>
          </div>
          <h2 className="text-xl font-black text-white tracking-tight">Pesan dari ASA</h2>
          <p className="text-xs text-blue-100/90 max-w-md mx-auto mt-1 leading-relaxed">
            Apakah Anda yakin barang pesanan ini sudah sampai di gudang/pabrik? Sistem akan mencatat penerimaan resmi dan <strong>menambahkan stok fisik</strong> ke inventori.
          </p>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto bg-slate-50/50">
          
          {/* PO & Vendor Info Banner */}
          <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-0.5">
                {requestData.po_number || 'PO Purchasing'}
              </p>
              <h4 className="font-bold text-slate-800 text-sm">
                {requestData.nama_perusahaan || requestData.vendor || requestData.supplier || 'Supplier'}
              </h4>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold text-slate-400 block tracking-wider uppercase">PIC Pengaju</span>
              <span className="text-xs font-black text-slate-700">{requestData.pic || 'Purchasing Staff'}</span>
            </div>
          </div>

          {/* Items & Stock Calculation List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black text-slate-600 uppercase tracking-wider">
                ▸ Rincian Barang & Kalkulasi Stok ({items.length} Item)
              </span>
            </div>

            <div className="space-y-2.5">
              {items.map((it, idx) => (
                <div key={idx} className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-sm hover:border-blue-300 transition-all">
                  {/* Header Row: Name & Aesthetic Status Badges */}
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                      <h5 className="font-bold text-slate-800 text-sm tracking-tight">{it.nama}</h5>
                      <span className="text-[10px] text-slate-400 font-semibold tracking-wide">{it.kategori || it.masterCategory}</span>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {/* Status Keberadaan di Inventori (Aesthetic Typography Badge) */}
                      {it.isRegistered ? (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
                          ✓ Ada di Master
                        </span>
                      ) : (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-bold">
                          ✦ Item Baru
                        </span>
                      )}

                      {/* Status Keaktifan Inventori dengan Simbol Estetik */}
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider ${
                        it.statusTheme === 'active'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : it.statusTheme === 'passive'
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : it.statusTheme === 'new'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`}>
                        {it.statusSymbol} {it.inventoryStatus}
                      </span>
                    </div>
                  </div>

                  {/* Stock Progression Card (Clean Numeric Layout) */}
                  <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 grid grid-cols-3 gap-2 items-center text-center">
                    <div>
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Stok Saat Ini</span>
                      <span className="text-xs font-bold text-slate-700">{fmt(it.currentStock)} {it.satuan}</span>
                    </div>

                    <div className="border-x border-slate-200/80 px-1">
                      <span className="text-[9px] font-black text-blue-600 uppercase tracking-wider block mb-0.5">+ Datang</span>
                      <span className="text-xs font-black text-blue-600">+{fmt(it.incomingQty)} {it.satuan}</span>
                    </div>

                    <div className="bg-emerald-50/80 rounded-lg py-1 border border-emerald-200/60">
                      <span className="text-[9px] font-black text-emerald-700 uppercase tracking-wider block mb-0.5">Total Stok Baru</span>
                      <span className="text-xs font-black text-emerald-700">{fmt(it.finalStock)} {it.satuan}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ekspedisi & Catatan Inputs */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 shadow-sm">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">
                Ekspedisi Pengiriman / Kurir
              </label>
              <Input
                value={ekspedisi}
                onChange={(e) => setEkspedisi(e.target.value)}
                placeholder="Contoh: J&T Cargo, Dakota, Lalamove, Kurir Vendor"
                className="h-9 text-xs bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">
                Catatan Penerimaan (Opsional)
              </label>
              <Input
                value={catatan}
                onChange={(e) => setCatatan(e.target.value)}
                placeholder="Kondisi barang, nomor resi, atau catatan gudang..."
                className="h-9 text-xs bg-slate-50 border-slate-200"
              />
            </div>
          </div>

        </div>

        {/* Modal Action Footer */}
        <div className="p-4 bg-white border-t border-slate-200 flex items-center justify-end gap-2.5">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isProcessing}
            className="h-11 px-5 rounded-xl font-bold border-slate-200 text-slate-600 hover:bg-slate-50 text-xs"
          >
            ✕ Batal
          </Button>

          <Button
            onClick={handleConfirmArrival}
            disabled={isProcessing}
            className="h-11 px-6 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md shadow-blue-500/20 text-xs flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Memproses & Menambah Stok...
              </>
            ) : (
              '✓ Konfirmasi & Tambah ke Stok'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
